export type SamiJourneyPersona = "app_developer" | string;

export type SamiJourneyConfidence = "high" | "medium" | "low";

export interface SamiJourneyRecommendedProduct {
  uuid: string;
  name: string;
  reason?: string;
}

export interface SamiJourneyNavigateAction {
  type: "navigate";
  path: string;
}

export interface SamiJourneyAgentHandoffAction {
  type: "agent_handoff";
  agentId: string;
  path: string;
  tab?: string;
  initialPrompt: string;
  productUuid?: string;
}

export type SamiJourneyAction =
  | SamiJourneyNavigateAction
  | SamiJourneyAgentHandoffAction;

export interface SamiJourneyStep {
  id: string;
  title: string;
  description: string;
  action: SamiJourneyAction;
}

export interface SamiJourneyPayload {
  version: number;
  persona: SamiJourneyPersona;
  intentSummary: string;
  confidence?: SamiJourneyConfidence;
  recommendedProducts: SamiJourneyRecommendedProduct[];
  steps: SamiJourneyStep[];
}

const SAMI_JOURNEY_FENCE_RE = /```sami-journey\s*([\s\S]*?)```/gi;
const JSON_FENCE_RE = /```json\s*([\s\S]*?)```/gi;

function isSamiJourneyPayload(value: unknown): value is SamiJourneyPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as SamiJourneyPayload;
  return (
    typeof payload.version === "number" &&
    typeof payload.intentSummary === "string" &&
    Array.isArray(payload.steps) &&
    payload.steps.length > 0
  );
}

function tryParseJourneyJson(text: string): SamiJourneyPayload | null {
  try {
    const parsed = JSON.parse(text.trim()) as unknown;
    return isSamiJourneyPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseJourneyFromFenceMatches(
  content: string,
  fenceRe: RegExp,
): SamiJourneyPayload | null {
  for (const match of content.matchAll(fenceRe)) {
    const parsed = tryParseJourneyJson(match[1]);
    if (parsed) return parsed;
  }
  return null;
}

export function parseSamiJourney(content: string): SamiJourneyPayload | null {
  return (
    parseJourneyFromFenceMatches(content, SAMI_JOURNEY_FENCE_RE) ??
    parseJourneyFromFenceMatches(content, JSON_FENCE_RE)
  );
}

/** Hide journey JSON fences from concierge chat markdown (parsers still use raw content). */
export function stripSamiJourneyBlocksFromContent(content: string): string {
  let text = content.replace(SAMI_JOURNEY_FENCE_RE, "");
  text = text.replace(JSON_FENCE_RE, (full, inner) =>
    tryParseJourneyJson(inner) ? "" : full,
  );
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

/** Replace `{uuid}` placeholders in journey paths with a concrete product uuid. */
export function resolveJourneyPath(
  path: string,
  productUuid?: string | null,
): string {
  if (!productUuid) return path;
  return path.replace(/\{uuid\}/g, productUuid);
}

export function isAgentHandoffAction(
  action: SamiJourneyAction,
): action is SamiJourneyAgentHandoffAction {
  return action.type === "agent_handoff";
}
